/**
 * All canonical headers recognized by the system.
 */
const CANONICAL_HEADERS = [
  'cusip',
  'clientId',
  'ticker',
  'sedol',
  'isin',
  'identifier',
  'identifierType',
  'weight',
  'marketValue',
  'quantity',
  'notionalMarketValue',
  'costBasis',
  'purchaseDate',
  'portfolioID',
  'currency',
  'market',
] as const;

type HeaderMappings = Record<string, string[]>;

function isCanonicalHeader(header: string): header is (typeof CANONICAL_HEADERS)[number] {
  return CANONICAL_HEADERS.includes(header as (typeof CANONICAL_HEADERS)[number]);
}

/**
 * Compute Levenshtein edit distance between two strings.
 * Case-insensitive comparison.
 */
export function editDistance(a: string, b: string): number {
  const aLower = a.toLowerCase();
  const bLower = b.toLowerCase();

  if (aLower === bLower) {
    return 0;
  }
  if (aLower.length === 0) {
    return bLower.length;
  }
  if (bLower.length === 0) {
    return aLower.length;
  }

  const matrix: number[][] = [];

  for (let i = 0; i <= bLower.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= aLower.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= bLower.length; i++) {
    for (let j = 1; j <= aLower.length; j++) {
      if (bLower.charAt(i - 1) === aLower.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1 // deletion
        );
      }
    }
  }

  return matrix[bLower.length][aLower.length];
}

export interface FuzzyMatchResult {
  confidence: 'exact' | 'fuzzy' | 'none';
  distance?: number;
  match: string | null;
}

function normalizeHeaderForMatch(value: string): string {
  // Normalize here so exact and fuzzy matching use the same comparison rules.
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

/**
 * Build the list of raw header strings that are allowed to match.
 *
 * @example
 * ```ts
 * buildHeaderCandidates({
 *   identifier: ['ID', 'Ticker/Symbol'],
 * });
 * // => ['cusip', 'clientId', ..., 'identifier', 'ID', 'Ticker/Symbol']
 * ```
 */
function buildHeaderCandidates(fileHeaderMappings?: HeaderMappings): string[] {
  const candidates: string[] = [...CANONICAL_HEADERS];

  if (!fileHeaderMappings) {
    return candidates;
  }

  /*
   * Take every configured key and every configured alias and
   * treat them all as valid inputs for matching.
   */
  Object.entries(fileHeaderMappings).forEach(([resolvedHeader, aliases]) => {
    if (!isCanonicalHeader(resolvedHeader)) {
      return;
    }

    candidates.push(resolvedHeader, ...aliases);
  });

  // Remove duplicates so we do not compare the same logical candidate multiple times.
  return Array.from(new Set(candidates));
}

/**
 * Build a lookup from any normalized raw header to the canonical header key.
 *
 * @example
 * ```ts
 * buildResolvedHeaderLookup({
 *   identifier: ['ID', 'Ticker/Symbol'],
 * });
 * // => {
 * //   identifier: 'identifier',
 * //   id: 'identifier',
 * //   'ticker/symbol': 'identifier',
 * //   ...
 * // }
 * ```
 */
function buildResolvedHeaderLookup(fileHeaderMappings?: HeaderMappings): Record<string, string> {
  const resolvedHeaderLookup: Record<string, string> = {};

  // Canonical headers resolve to themselves.
  CANONICAL_HEADERS.forEach((header) => {
    resolvedHeaderLookup[normalizeHeaderForMatch(header)] = header;
  });

  if (!fileHeaderMappings) {
    return resolvedHeaderLookup;
  }

  // Configured keys also resolve to themselves, and every alias resolves to its key.
  Object.entries(fileHeaderMappings).forEach(([resolvedHeader, aliases]) => {
    if (!isCanonicalHeader(resolvedHeader)) {
      return;
    }

    resolvedHeaderLookup[normalizeHeaderForMatch(resolvedHeader)] = resolvedHeader;
    aliases.forEach((alias) => {
      resolvedHeaderLookup[normalizeHeaderForMatch(alias)] = resolvedHeader;
    });
  });

  return resolvedHeaderLookup;
}

/**
 * Match a raw file header to the canonical header key used by the importer.
 */
export function matchHeader(
  header: string,
  fileHeaderMappings?: HeaderMappings,
  maxDistance: number = 2
): FuzzyMatchResult {
  /*
   * Build the list of allowed inputs and a reverse lookup back to the canonical
   * header that the importer should ultimately use.
   */
  const candidates = buildHeaderCandidates(fileHeaderMappings);
  const resolvedHeaderLookup = buildResolvedHeaderLookup(fileHeaderMappings);
  const trimmedLowerHeader = header.trim().toLowerCase();
  const normalizedHeader = normalizeHeaderForMatch(header);

  // Skip empty or custom headers
  if (!normalizedHeader || trimmedLowerHeader.startsWith('custom.')) {
    return { match: null, confidence: 'none' };
  }

  // Snake case headers are not supported for import header matching
  if (trimmedLowerHeader.includes('_')) {
    return { match: null, confidence: 'none' };
  }

  const exactMatch = resolvedHeaderLookup[normalizedHeader];
  if (exactMatch) {
    return { match: exactMatch, confidence: 'exact', distance: 0 };
  }

  // If there is no exact match, keep track of the closest allowed input.
  let bestCandidate: string | null = null;
  let bestDistance = Infinity;
  let ambiguous = false;

  /*
   * After the exact pass, run the same distance check against the full set of
   * allowed values (canonical headers, config keys, and config aliases).
   */
  for (const candidate of candidates) {
    const distance = editDistance(normalizedHeader, normalizeHeaderForMatch(candidate));

    if (distance < bestDistance) {
      bestDistance = distance;
      bestCandidate = candidate;
      ambiguous = false;
    } else if (distance === bestDistance && bestCandidate !== candidate) {
      /*
       * Equal distances are only a problem when they resolve to different
       * canonical headers. If two aliases belong to the same resolved header,
       * we can still safely accept that result.
       */
      const resolvedBestCandidate = bestCandidate ? resolvedHeaderLookup[normalizeHeaderForMatch(bestCandidate)] : null;
      const resolvedCandidate = resolvedHeaderLookup[normalizeHeaderForMatch(candidate)];

      if (resolvedBestCandidate !== resolvedCandidate) {
        ambiguous = true;
      }
    }
  }

  if (ambiguous || bestDistance > maxDistance) {
    return { match: null, confidence: 'none' };
  }

  /*
   * Convert the closest raw candidate back into the canonical header key that
   * downstream import logic expects.
   */
  return {
    match: bestCandidate ? resolvedHeaderLookup[normalizeHeaderForMatch(bestCandidate)] : null,
    confidence: 'fuzzy',
    distance: bestDistance,
  };
}
